/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
    addCard,
    deleteSavedCard,
    getNewWalletToken,
    getWallet,
    postUnlockChallengeAnswer,
    putCardUpdate,
} from './digital-wallet-api';

export const WALLET_KEY = 'wallet';

export function useWalletQuery(enabled: boolean) {
    return useQuery({
        queryKey: [WALLET_KEY],
        queryFn: async ({ signal }) => getWallet(signal),
        staleTime: 0,
        refetchOnWindowFocus: true,
        retry: false,
        enabled,
    });
}

export function useWalletTokenQuery(enabled: boolean) {
    const query = useQuery({
        staleTime: 9 * 60 * 1000, // 9 minutes since the token expires in 10 minutes
        refetchOnWindowFocus: true,
        queryKey: [`${WALLET_KEY}-token`],
        queryFn: () => getNewWalletToken(),
        retry: false,
        enabled,
    });
    return { ...query, refetch: query.refetch };
}

export function useDeleteCardMutation({
    onSuccess,
    onSettled,
}: {
    onSuccess?: () => void;
    onSettled?: () => void;
} = {}) {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (entryId: string) => deleteSavedCard(entryId),
        onSuccess: () => {
            if (onSuccess) {
                onSuccess();
            }
        },
        onSettled: async () => {
            await queryClient.invalidateQueries({ queryKey: [WALLET_KEY] });
            await queryClient.invalidateQueries({ queryKey: [`${WALLET_KEY}-token`] });
            if (onSettled) {
                onSettled();
            }
        },
    });
}

export function useUpdateCardMutation({
    onSuccess,
    onSettled,
}: {
    onSuccess?: () => void;
    onSettled?: () => void;
} = {}) {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({
            entryId,
            nickname,
            isDefault,
        }: {
            entryId: string;
            nickname: string;
            isDefault: boolean;
        }) => putCardUpdate({ entryId, nickname, isDefault }),
        onSuccess: () => {
            if (onSuccess) {
                onSuccess();
            }
        },
        onSettled: async () => {
            await queryClient.invalidateQueries({ queryKey: [WALLET_KEY] });

            if (onSettled) {
                onSettled();
            }
        },
    });
}

export function useAddCardMutation({
    onSuccess,
    onSettled,
}: {
    onSuccess?: () => void;
    onSettled?: () => void;
} = {}) {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({
            walletToken,
            nickname,
            isDefault,
            cardData,
        }: {
            walletToken: string;
            nickname: string;
            isDefault: boolean;
            cardData: {
                number: string;
                expiration_month: number;
                expiration_year: number;
                validation_code: string;
            };
        }) => addCard({ walletToken, nickname, isDefault, cardData }),
        onSuccess: () => {
            if (onSuccess) {
                onSuccess();
            }
        },
        onSettled: async () => {
            await queryClient.invalidateQueries({ queryKey: [WALLET_KEY] });
            if (onSettled) {
                onSettled();
            }
        },
    });
}

export function useUnlockChallengeMutation({
    onSuccess,
    onSettled,
}: {
    onSuccess?: () => void;
    onSettled?: () => void;
} = {}) {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({
            lastFour,
            expiration,
            internal_account_id,
            account_id,
        }: {
            lastFour: string;
            expiration: string | null;
            internal_account_id: string | null;
            account_id: string | null;
        }) =>
            postUnlockChallengeAnswer({
                lastFour,
                expiration,
                internal_account_id,
                account_id,
            }),
        onSuccess: () => {
            if (onSuccess) {
                onSuccess();
            }
        },
        onSettled: async () => {
            await queryClient.invalidateQueries({ queryKey: [WALLET_KEY] });
            if (onSettled) {
                onSettled();
            }
        },
    });
}
